import React, { useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';

import { API_BASE } from '../lib/api';
import { smartCompressImage } from '../lib/imageCompression';

interface UploadStatus {
  state: 'idle' | 'uploading' | 'success' | 'error';
  message?: string;
}

const PaymentProofUpload: React.FC = () => {
  const { t } = useTranslation();
  const { orderNum } = useParams<{ orderNum: string }>();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [status, setStatus] = useState<UploadStatus>({ state: 'idle' });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
      setStatus({ state: 'idle' });
    }
  };

  const handleUpload = async () => {
    if (!file || !orderNum) {
      setStatus({ state: 'error', message: t('order:upload.chooseFile') });
      return;
    }

    setCompressing(true);
    setUploading(true);
    setStatus({ state: 'uploading' });

    try {
      let uploadFile: File = file;
      if (file.type.startsWith('image/')) {
        try {
          const result = await smartCompressImage(file);
          uploadFile = result.file;
        } catch {
          // 壓縮失敗時回退原檔
        }
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('orderNum', orderNum);

      const response = await fetch(`${API_BASE}/api/public/payment-proof/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setStatus({
          state: 'success',
          message: t('order:upload.success')
        });
        setFile(null);
        setPreview('');
      } else {
        const error = await response.json();
        setStatus({ state: 'error', message: error.error || t('order:upload.failed') });
      }
    } catch (error) {
      setStatus({ state: 'error', message: t('order:upload.uploadError') });
    } finally {
      setCompressing(false);
      setUploading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-lg w-full">
          <div className="text-center mb-6">
            <Upload size={56} className="mx-auto text-blue-500 mb-4" />
            <h1 className="text-3xl font-bold text-gray-800">{t('order:upload.title')}</h1>
            <p className="text-xl text-gray-600 mt-2">{t('order:upload.orderNumHash', { orderNum })}</p>
          </div>

          {status.state === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 flex gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0" size={28} />
              <div>
                <p className="font-bold text-xl text-green-700">{t('order:upload.uploadSuccess')}</p>
                <p className="text-lg text-green-600">{status.message}</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-3 bg-green-600 text-white text-lg font-bold py-3 px-6 rounded-xl hover:bg-green-700"
                >
                  {t('order:confirmation.backHome')}
                </button>
              </div>
            </div>
          )}

          {status.state === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 flex gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0" size={28} />
              <div>
                <p className="font-bold text-xl text-red-700">{t('order:upload.failed')}</p>
                <p className="text-lg text-red-600">{status.message}</p>
              </div>
            </div>
          )}

          {status.state !== 'success' && (
            <>
              <div className="bg-white rounded-xl border-2 border-dashed border-blue-300 p-6 mb-6">
                {preview ? (
                  <div className="text-center">
                    <img src={preview} alt="Preview" className="max-h-48 mx-auto mb-4 rounded-xl" />
                    <p className="text-lg text-gray-600 mb-4 break-words">{file?.name}</p>
                    <button onClick={() => setFile(null)} className="text-blue-500 hover:text-blue-700 text-lg font-medium">
                      {t('order:upload.changeFile')}
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={48} className="mx-auto text-blue-400 mb-3" />
                    <p className="text-xl text-gray-700 font-bold mb-2">{t('order:upload.dropzone')}</p>
                    <p className="text-lg text-gray-500 mb-4">{t('order:upload.fileHint')}</p>
                    <input type="file" accept="image/*,.pdf" onChange={handleFileSelect} disabled={uploading} className="hidden" id="file-input" />
                    <label htmlFor="file-input" className="inline-block bg-blue-500 hover:bg-blue-600 text-white text-xl font-bold py-3 px-8 rounded-xl cursor-pointer transition-all">
                      {t('order:upload.selectFile')}
                    </label>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                <p className="font-bold text-xl text-blue-900 mb-3">{t('order:upload.guideTitle')}</p>
                <ul className="text-lg text-gray-700 space-y-2">
                  <li>{t('order:upload.guide1')}</li>
                  <li>{t('order:upload.guide2')}</li>
                  <li>{t('order:upload.guide3')}</li>
                  <li>{t('order:upload.guide4')}</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading || compressing}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {(compressing || uploading) && <Loader size={24} className="animate-spin" />}
                  {compressing ? t('order:upload.compressing') : uploading ? t('order:upload.uploading') : t('order:upload.title')}
                </button>
                <button
                  onClick={() => navigate('/')}
                  disabled={uploading}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={24} />
                  {t('order:upload.cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    <SiteFooter />
    </>
  );
};

export default PaymentProofUpload;
